<template>
  <div>
    <h2 class="title">{{ $t('login.title_edit') }}</h2>
    <el-form
      ref="form"
      :model="form"
      :rules="loginRules"
      labelPosition="top"
      label-width="80px"
      :hide-required-asterisk="true"
    >
      <template v-if="step === 1">
        <el-form-item prop="email">
          <label class="label" slot="label">{{ $t('login.email') }}</label>
          <el-input
            placeholder="Email"
            type="email"
            size="large"
            v-model="form.email"
            @keyup.enter.native="submitStep1"
          ></el-input>
        </el-form-item>
        <el-form-item prop="newpassword">
          <label class="label" slot="label">{{ $t('login.npsd') }}</label>
          <el-input
            v-model="form.newpassword"
            placeholder="Password"
            type="password"
            @keyup.enter.native="submitStep1"
          ></el-input>
          <password-checklist :password="form.newpassword" />
        </el-form-item>
        <el-form-item prop="psdCheck">
          <label class="label" slot="label">{{ $t('login.psdCheck') }}</label>
          <el-input
            v-model="form.psdCheck"
            placeholder="Password"
            type="password"
            @keyup.enter.native="submitStep1"
          ></el-input>
        </el-form-item>
        <a
          class="button mb-16 is-info is-primary is-fullwidth is-medium"
          @click="submitStep1"
          :class="{ 'is-loading': isValidating, 'is-disabled-link': isCoolingDown }"
          >{{ isCoolingDown ? `發送驗證碼（${resendCooldown}s）` : '發送驗證碼' }}</a
        >
      </template>
      <template v-else>
        <el-form-item>
          <label class="label" slot="label">{{ $t('login.email') }}</label>
          <el-input :value="form.email" disabled size="large"></el-input>
        </el-form-item>
        <el-form-item prop="otp">
          <label class="label" slot="label">驗證碼</label>
          <el-input
            v-model="form.otp"
            placeholder="請輸入驗證碼（有效期 15 分鐘）"
            maxlength="6"
            @keyup.enter.native="submitStep2"
          ></el-input>
        </el-form-item>
        <a
          class="button mb-16 is-info is-primary is-fullwidth is-medium"
          @click="submitStep2"
          :class="{ 'is-loading': isValidating }"
          >{{ $t('login.title_edit') }}</a
        >
        <a
          class="button is-fullwidth is-medium"
          @click="sendOtp"
          :class="{ 'is-loading': isValidating, 'is-disabled-link': isCoolingDown }"
          >{{ isCoolingDown ? `重新發送驗證碼（${resendCooldown}s）` : '重新發送驗證碼' }}</a
        >
      </template>
      <span class="tips has-text-grey">Please contact geosupply.tw@gmail.com for demo pass</span>
    </el-form>
  </div>
</template>

<script>
import PasswordChecklist from '@/components/common/PasswordChecklist.vue';
import {
  readOtpCooldown,
  writeOtpCooldown,
  OTP_RESEND_COOLDOWN_SECONDS,
} from '@/lib/base/otpCooldown';

export default {
  name: 'ClientPage',
  components: { PasswordChecklist },
  data() {
    const validatePass = (rule, value, callback) => {
      if (!value) { callback(new Error('請輸入新密碼')); return; }
      if (value.length < 12) { callback(new Error('密碼至少需要 12 個字元')); return; }
      if (!/[A-Z]/.test(value)) { callback(new Error('密碼需包含至少一個大寫字母（A–Z）')); return; }
      if (!/[a-z]/.test(value)) { callback(new Error('密碼需包含至少一個小寫字母（a–z）')); return; }
      if (!/[0-9]/.test(value)) { callback(new Error('密碼需包含至少一個數字（0–9）')); return; }
      if (!/[^A-Za-z0-9]/.test(value)) { callback(new Error('密碼需包含至少一個特殊字元（!@#$%^&* 等）')); return; }
      callback();
    };
    const checkValidatePass = (rule, value, callback) => {
      if (value != this.form.newpassword) {
        callback(new Error(this.$t('login.psdCheckValidate')));
      } else {
        callback();
      }
    };
    return {
      step: 1,
      form: {
        email: null,
        newpassword: null,
        lastPassword: null,
        otp: null,
      },
      loginRules: {
        email: [
          { required: true, message: '請輸入郵箱', trigger: 'blur' },
          { type: 'email', message: '請輸入正確的郵箱', trigger: 'blur' },
        ],
        newpassword: [{ required: true, trigger: 'blur', validator: validatePass }],
        psdCheck: [{ required: true, trigger: 'blur', validator: checkValidatePass }],
        otp: [{ required: true, message: '請輸入驗證碼', trigger: 'blur' }],
      },
      isValidating: false,
      resendCooldown: 0,
      cooldownEmail: null,
      cooldownTimer: null,
    };
  },
  computed: {
    isCoolingDown() {
      return (
        this.resendCooldown > 0 &&
        !!this.cooldownEmail &&
        !!this.form.email &&
        this.cooldownEmail.trim().toLowerCase() === this.form.email.trim().toLowerCase()
      );
    },
  },
  mounted() {
    if (this.$route.query.form) {
      let form = this.$route.query.form;
      if (form.email) this.form.email = form.email;
      if (form.password) this.form.lastPassword = form.password;
    }
    this.restoreCooldown();
  },
  beforeDestroy() {
    clearInterval(this.cooldownTimer);
  },
  methods: {
    restoreCooldown() {
      const cooldown = readOtpCooldown();
      if (!cooldown) return;
      this.cooldownEmail = cooldown.email;
      this.startCooldownTimer(cooldown.remain);
    },
    startCooldownTimer(seconds) {
      clearInterval(this.cooldownTimer);
      this.resendCooldown = seconds;
      this.cooldownTimer = setInterval(() => {
        this.resendCooldown -= 1;
        if (this.resendCooldown <= 0) {
          clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
          this.cooldownEmail = null;
        }
      }, 1000);
    },
    submitStep1() {
      this.$refs.form.validate(valid => {
        if (valid) this.sendOtp();
      });
    },
    submitStep2() {
      this.$refs.form.validate(valid => {
        if (valid) this.editUser();
      });
    },
    async sendOtp() {
      if (this.isValidating || this.isCoolingDown) return;
      this.isValidating = true;
      this.form.otp = null;
      try {
        await this.$axios({
          category: 'user',
          apiName: 'requestPasswordReset',
          method: 'post',
          data: { email: this.form.email },
        });
        this.$notifySuccess('驗證碼已發送，請查收信箱');
        this.step = 2;
      } catch (error) {
        const status = error.response?.status;
        const data = error.response?.data;
        if (status === 400) {
          const rawMsg = data?.message;
          const msg = Array.isArray(rawMsg) ? rawMsg.join('、') : rawMsg;
          this.$notifyError(msg || '請求失敗');
        } else if (status === 404) {
          this.$notifyError('查無此帳號');
        } else {
          this.$notifyError('發送驗證碼失敗，請稍後再試');
        }
      } finally {
        this.isValidating = false;
        writeOtpCooldown(this.form.email, OTP_RESEND_COOLDOWN_SECONDS);
        this.cooldownEmail = this.form.email;
        this.startCooldownTimer(OTP_RESEND_COOLDOWN_SECONDS);
      }
    },
    async editUser() {
      this.isValidating = true;
      try {
        const res = await this.$axios({
          category: 'user',
          apiName: 'resetPassword',
          method: 'put',
          data: {
            email: this.form.email,
            otp: this.form.otp,
            newPassword: this.form.newpassword,
          },
        });
        const msg = res.data?.message || '密碼已重設，請重新登入';
        await this.$store.dispatch('user/logoutUser');
        this.$notifySuccess(msg);
        this.$router.push({ name: 'Login' });
      } catch (error) {
        const status = error.response?.status;
        const data = error.response?.data;
        if (status === 400) {
          const rawMsg = data?.message;
          const msg = Array.isArray(rawMsg) ? rawMsg.join('、') : rawMsg;
          this.$notifyError(msg || '密碼格式不符規定，請修改密碼');
        } else if (status === 401) {
          const rawMsg = data?.message;
          const msg = Array.isArray(rawMsg) ? rawMsg.join('、') : rawMsg;
          this.$notifyError(msg || '驗證碼錯誤或已過期');
        } else if (status === 500) {
          if (typeof data === 'string' && data) {
            this.$notifyError(data);
          } else {
            this.$notifyError('伺服器錯誤，請稍後再試');
          }
        } else {
          this.$notifyError('操作失敗，請稍後再試');
        }
      } finally {
        this.isValidating = false;
      }
    },
  },
};
</script>

<style scoped>
.is-disabled-link {
  pointer-events: none;
  opacity: 0.5;
}
</style>
